/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';

import Card from '../components/Card';
import Form from '../components/Form';
import { FILE_IMPORT, FILE_IMPORT_OPTIONS } from '../enum/object';
import populator from '../utils/populator';

import ContextHelpPopOver from '../components/ContextHelpPopOver';

import { StepFooter, UsersTable } from './commons';

import './modals.scss';

function NewUserCSVImport({ onFinish, navigateBack }) {
  const roles = useSelector((state) => state.ecosystems.dictionaries.groups || []);
  const [importStage, setImportStage] = useState(0);
  const [importMethod, setImportMethod] = useState(FILE_IMPORT_OPTIONS[0]);
  const [fileContent, setFileContent] = useState('');
  const [includeColHeaders, setIncludeColHeaders] = useState(true);
  const [addedUsers, setAddedUsers] = useState([]);
  const [errors, setErrors] = useState([]);

  const processData = (content) => {
    let lines = content
      .split(/\r\n|\n/)
      .filter((data) => data !== '')
      .map((data) => data.split(';'));
    if (includeColHeaders) {
      lines = lines.slice(1);
    }

    const users = lines
      .map((row) => row[0].split(','))
      .filter((p) => p[0].length === 5)
      .map((data) => ({
        firstName: data[0],
        lastName: data[1],
        role: data[2],
        email: data[3],
        password: data[4],
        status: 'Pending',
        securityGroup: '',
      }))
      .filter((user) => roles.map(populator.role).find((role) => role.label === user.role))
      .map((user) => ({
        ...user,
        role: roles.map(populator.role).find((role) => role.label === user.role),
      }));

    if (users.length === 0) {
      setAddedUsers([]);
      setErrors(['No users to import']);
      return;
    }

    setAddedUsers(users);
    setImportStage(1);
  };

  const fileReadingFinished = (event) => {
    setFileContent(event.target.result);
    processData(event.target.result);
  };

  const errorHandler = (event) => {
    if (event.target.error.name === 'NotReadableError') {
      setErrors(['Cannot read file!']);
    }
  };

  const getAsText = (fileToRead) => {
    const reader = new FileReader();
    reader.readAsText(fileToRead);
    reader.onload = fileReadingFinished;
    reader.onerror = errorHandler;
  };

  const handleFiles = (event) => {
    if (window.FileReader) {
      getAsText(event.target.files[0]);
    } else {
      // eslint-disable-next-line no-alert
      alert('FileReader are not supported in this browser.');
    }
  };

  const handleSubmit = async () => {
    if (!importStage) {
      if (!fileContent) {
        setErrors([
          importMethod === FILE_IMPORT_OPTIONS[0]
            ? 'File Content is empty.'
            : 'Please upload a valid file with some users',
        ]);
      } else {
        processData(fileContent);
      }
      return;
    }

    try {
      onFinish({ type: 'user', data: addedUsers });
    } catch (err) {
      setErrors(['Failed to import Users']);
    }
  };

  const goBack = () => {
    if (importStage === 1) {
      setImportStage(0);
      setErrors([]);
      return;
    }

    navigateBack();
  };

  const removeUser = (index) => {
    setAddedUsers((prevAddedUsers) => prevAddedUsers.filter((vl, ind) => ind !== index));
    if (addedUsers.length === 1) {
      setFileContent('');
    }
  };

  const changeField = (key, value) => {
    if (key === 'importMethod') {
      setImportMethod(value);
      setFileContent('');
      setErrors([]);
      return;
    }

    if (key === 'includeColHeaders') {
      setIncludeColHeaders(value);
      return;
    }

    if (key === 'fileContent') {
      setFileContent(value);
    }
  };

  const renderConfig = () => (
    <div className="modal__content padded ldap-config new-user-survey-csv">
      {errors.map((err, index) => (
        <p className="error" key={index}>
          {err}
        </p>
      ))}
      <Card header={false}>
        <div className="ldap-config-connection">
          <h5 className="form-header desc-header">
            Add users to your ecosystem to let them connect using their access credentials.
          </h5>
        </div>
        <div className="form-body">
          <div className="header-block">
            <h5>
              Comma Seperated Value(CSV) file must match our format requirements.&nbsp;
              <a
                href={`${process.env.REACT_APP_KB_URL}/how-to/quickstart/import-users/`}
                target="_blank"
                rel="noopener noreferrer"
              >
                See Docs
              </a>
            </h5>
          </div>
          <div className="form-row gateway-config">
            <Grid container>
              <Grid container item xs={12} alignItems="baseline">
                <Grid item xs="auto">
                  <p className="custom-header">Import Method</p>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Form.Group horizontal>
                    <Form.Select
                      value={importMethod}
                      placeholder=""
                      onChange={(value) => {
                        changeField('importMethod', value);
                      }}
                      options={FILE_IMPORT_OPTIONS}
                    />
                  </Form.Group>
                </Grid>
                <Grid item xs="auto">
                  <ContextHelpPopOver helpId="USER_IMPORT_CSV" />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <>
                  {importMethod.value === FILE_IMPORT.FILE_CONTENT ? (
                    <Form.Group horizontal>
                      <Form.Text
                        value={fileContent}
                        onChange={(value) => changeField('fileContent', value)}
                        placeholder={`First name;Last name;Role;Email;Password;
${includeColHeaders ? 'John;Doe;Role name;v@v.com;somepassword' : ''}`}
                        multiline
                      />
                    </Form.Group>
                  ) : (
                    <div>
                      <input type="file" onChange={handleFiles} accept=".csv" />
                    </div>
                  )}
                </>
                <Form.Group full extraClass="headerCheckbox">
                  <Form.Checkbox
                    checked={includeColHeaders}
                    onChange={(value) => {
                      changeField('includeColHeaders', value);
                    }}
                    label="First line includes column Headers"
                    extraClass="startTLS"
                  ></Form.Checkbox>
                </Form.Group>
              </Grid>
            </Grid>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderTable = () => (
    <div className="modal__content padded ldap-config new-user-survey-csv">
      {errors.map((err, index) => (
        <p className="error" key={index}>
          {err}
        </p>
      ))}
      <Card header={false}>
        <div className="ldap-config-connection">
          <h5 className="form-header desc-header">Users to be Imported</h5>
        </div>
        <div className="form-body">
          <div className="form-row gateway-config">
            <Grid container>
              <Grid item xs={12}>
                <UsersTable users={addedUsers} removeUser={removeUser}></UsersTable>
              </Grid>
            </Grid>
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <div>
      {renderConfig()}
      {addedUsers.length ? renderTable() : null}
      <div className="wedge-modal__footer">
        <StepFooter edit={false} submit onClick={handleSubmit} onBack={goBack} />
      </div>
    </div>
  );
}

NewUserCSVImport.propTypes = {
  onFinish: PropTypes.func.isRequired,
  navigateBack: PropTypes.func.isRequired,
};

NewUserCSVImport.Footer = StepFooter;

export default NewUserCSVImport;
